// src/modules/assetManagement/linkRules.ts
// An asset links to an Event, or to a Product inside an Event it is also linked to.
// Products belong to Events (event.productIds), so every linked product must be covered by a linked event.

export interface EventLike {
  id: string;
  title: string;
  productIds: string[];
}

export interface LinkSelection {
  eventIds: string[];
  productIds: string[];
}

export const eventsForProduct = (events: EventLike[], productId: string) =>
  events.filter((e) => e.productIds.includes(productId));

export const productIdsOfEvents = (events: EventLike[], eventIds: string[]) =>
  new Set(events.filter((e) => eventIds.includes(e.id)).flatMap((e) => e.productIds));

/** Products in the selection that no selected (or already linked) event contains */
export const uncoveredProducts = (events: EventLike[], sel: LinkSelection, extraEventIds: string[] = []) => {
  const covered = productIdsOfEvents(events, [...sel.eventIds, ...extraEventIds]);
  return sel.productIds.filter((p) => !covered.has(p));
};

/** Unticking an event also drops the products it was covering */
export const toggleEvent = (
  events: EventLike[],
  sel: LinkSelection,
  eventId: string,
  extraEventIds: string[] = [],
): LinkSelection => {
  if (!sel.eventIds.includes(eventId)) return { ...sel, eventIds: [...sel.eventIds, eventId] };
  const eventIds = sel.eventIds.filter((id) => id !== eventId);
  const covered = productIdsOfEvents(events, [...eventIds, ...extraEventIds]);
  return { eventIds, productIds: sel.productIds.filter((p) => covered.has(p)) };
};

/** Ticking a product picks its event automatically when it belongs to exactly one */
export const toggleProduct = (
  events: EventLike[],
  sel: LinkSelection,
  productId: string,
  extraEventIds: string[] = [],
): LinkSelection => {
  if (sel.productIds.includes(productId)) {
    return { ...sel, productIds: sel.productIds.filter((p) => p !== productId) };
  }
  const next = { ...sel, productIds: [...sel.productIds, productId] };
  const covered = productIdsOfEvents(events, [...sel.eventIds, ...extraEventIds]);
  if (covered.has(productId)) return next;
  const candidates = eventsForProduct(events, productId);
  return candidates.length === 1 ? { ...next, eventIds: [...next.eventIds, candidates[0].id] } : next;
};

/** Which events and products to offer, given what is ticked so far */
export const visibleOptions = (
  events: EventLike[],
  productIds: string[],
  sel: LinkSelection,
  extraEventIds: string[] = [],
) => {
  // Event chosen first: only that event's products. Otherwise any product that sits in some event.
  const allowedProducts = sel.eventIds.length
    ? productIdsOfEvents(events, [...sel.eventIds, ...extraEventIds])
    : new Set(events.flatMap((e) => e.productIds));
  // Product chosen first: only the events that contain it
  const allowedEvents = sel.productIds.length
    ? new Set(sel.productIds.flatMap((p) => eventsForProduct(events, p).map((e) => e.id)))
    : null;
  return {
    eventIds: events
      .filter((e) => !allowedEvents || allowedEvents.has(e.id) || sel.eventIds.includes(e.id))
      .map((e) => e.id),
    productIds: productIds.filter((p) => allowedProducts.has(p) || sel.productIds.includes(p)),
    filteredByEvent: sel.eventIds.length > 0,
    filteredByProduct: sel.productIds.length > 0,
  };
};

/** Make an asset's links consistent: every product needs an event that contains it */
export const coverAssetLinks = (events: EventLike[], eventIds: string[], productIds: string[]) => {
  const ids = [...eventIds];
  productIds.forEach((p) => {
    if (!productIdsOfEvents(events, ids).has(p)) {
      const first = eventsForProduct(events, p)[0];
      if (first) ids.push(first.id);
    }
  });
  return [...new Set(ids)];
};

/** After events are removed, keep only the products a remaining event still contains */
export const dropUncoveredProducts = (events: EventLike[], eventIds: string[], productIds: string[]) => {
  const covered = productIdsOfEvents(events, eventIds);
  return productIds.filter((p) => covered.has(p));
};
